import type { ReactNode } from "react";
import {
  FOOTER_BODY_FONT_STYLE,
  FOOTER_DATA_FONT_STYLE,
} from "./footer-toolbar-model";
import type { FooterTerminalController } from "./use-footer-terminal";

export function FooterTerminalPanel({
  terminal,
}: {
  terminal: FooterTerminalController;
}) {
  return (
    <div className="flex h-full flex-col">
      <div
        className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2 text-[11px]"
        style={FOOTER_DATA_FONT_STYLE}
      >
        {terminal.history.map((line, index) => (
          <div
            key={`${index}-${line.text}`}
            className={
              line.type === "input"
                ? "text-[var(--fintheon-accent)]"
                : "text-[var(--fintheon-muted)]"
            }
          >
            {line.type === "input"
              ? line.text
              : renderOutputLine(line.text, (command) => {
                  terminal.setInput(command);
                  terminal.inputRef.current?.focus();
                })}
          </div>
        ))}
        <div ref={terminal.terminalEndRef} />
      </div>
      <div className="relative shrink-0">
        <div className="flex items-center gap-1.5 border-t border-[var(--fintheon-accent)]/10 px-3 py-2">
          <span
            className="min-w-fit text-[11px] text-[var(--fintheon-accent)]/70"
            style={FOOTER_DATA_FONT_STYLE}
          >
            {terminal.prompt} $
          </span>
          <input
            ref={terminal.inputRef}
            value={terminal.input}
            onChange={(event) => terminal.onInputChange(event.target.value)}
            onKeyDown={terminal.handleKeyDown}
            className="flex-1 bg-transparent text-[11px] text-[var(--fintheon-accent)] placeholder:text-[var(--fintheon-muted)]/60 focus:outline-none"
            style={FOOTER_DATA_FONT_STYLE}
            placeholder="type a command or / for scripts..."
            spellCheck={false}
          />
        </div>
        {terminal.suggestionsOpen ? (
          <div
            className="fintheon-popover-surface absolute bottom-full left-0 right-0 z-50 max-h-48 overflow-y-auto"
            style={FOOTER_BODY_FONT_STYLE}
          >
            {terminal.suggestions.map((item, index) => (
              <button
                key={item.slug}
                type="button"
                onClick={() => terminal.useSuggestion(item)}
                className={`w-full px-3 py-1.5 text-left text-[11px] transition-colors ${
                  index === terminal.suggestionsIndex
                    ? "text-[var(--fintheon-accent)]"
                    : "text-[var(--fintheon-muted)] hover:text-[var(--fintheon-text)]"
                }`}
              >
                <span className="text-[var(--fintheon-accent)]/70">
                  /{item.slug}
                </span>
                <span className="ml-2 text-[var(--fintheon-text)]/65">
                  {item.label}
                </span>
                <span className="ml-3 inline-block max-w-[45%] truncate align-bottom text-[10px] text-[var(--fintheon-muted)]/75">
                  {item.command}
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function renderOutputLine(
  text: string,
  onClickCommand: (command: string) => void,
): ReactNode {
  const parts = text.split(/`([^`]+)`/);
  if (parts.length === 1) return text;
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <button
        key={`${index}-${part}`}
        type="button"
        onClick={() => onClickCommand(part)}
        className="cursor-pointer text-[var(--fintheon-accent)] hover:underline"
        title={`Click to fill: ${part}`}
      >
        {part}
      </button>
    ) : (
      <span key={`${index}-${part}`}>{part}</span>
    ),
  );
}
